import { Component } from '@angular/core';
import { FaceSwapService } from 'src/app/shared';
import { NgxSpinnerService } from 'ngx-spinner';
declare var Swal: any;

@Component({
  selector: 'app-face-swap',
  templateUrl: './face-swap.component.html',
  styleUrls: ['./face-swap.component.css']
})
export class FaceSwapComponent {

  pageTitle = 'สลับใบหน้า (Face Swap)';

  // รูปใบหน้าของผู้ใช้
  faceFile: File = null;
  facePreview: string = null;

  // รูปปลายทาง (target) ที่อัปโหลดเอง
  targetFile: File = null;
  targetPreview: string = null;

  // โหมดเลือกปลายทาง: 'upload' = อัปโหลดเอง, 'template' = เลือก template ฝั่ง server
  targetMode: 'upload' | 'template' = 'template';

  // รายชื่อ template ที่เตรียมไว้ฝั่ง server (ชื่อไฟล์ใน storage/app/public/face-swap/templates)
  templates: { name: string; label: string }[] = [
    { name: 'pilot.jpg', label: 'นักบิน' }
  ];
  selectedTemplate: string = 'pilot.jpg';

  // ผลลัพธ์
  resultUrl: string = null;
  processing = false;

  constructor(
    private faceSwapService: FaceSwapService,
    private spinner: NgxSpinnerService,
  ) { }

  // ─── เลือกรูปใบหน้า ─────────────────────────────────────────────
  onFaceSelected(event: any) {
    const file: File = event.target.files && event.target.files[0];
    if (!file) { return; }
    if (!this.isValidImage(file)) { return; }
    this.faceFile = file;
    this.readPreview(file, (dataUrl) => this.facePreview = dataUrl);
  }

  // ─── เลือกรูปปลายทาง (target) ────────────────────────────────────
  onTargetSelected(event: any) {
    const file: File = event.target.files && event.target.files[0];
    if (!file) { return; }
    if (!this.isValidImage(file)) { return; }
    this.targetFile = file;
    this.readPreview(file, (dataUrl) => this.targetPreview = dataUrl);
  }

  // ─── ประมวลผล ───────────────────────────────────────────────────
  submit() {
    if (!this.faceFile) {
      this.failDialog('กรุณาเลือกรูปใบหน้าของคุณก่อน');
      return;
    }

    let target: File = null;
    let template: string = null;

    if (this.targetMode === 'upload') {
      if (!this.targetFile) {
        this.failDialog('กรุณาเลือกรูปปลายทาง');
        return;
      }
      target = this.targetFile;
    } else {
      if (!this.selectedTemplate) {
        this.failDialog('กรุณาเลือก template');
        return;
      }
      template = this.selectedTemplate;
    }

    this.processing = true;
    this.resultUrl = null;
    this.spinner.show();

    this.faceSwapService.swap(this.faceFile, target, template).subscribe(res => {
      this.spinner.hide();
      this.processing = false;
      this.resultUrl = res && res.result_url ? res.result_url : null;
      if (this.resultUrl) {
        this.successDialog();
      } else {
        this.failDialog('ไม่พบรูปผลลัพธ์');
      }
    }, err => {
      this.spinner.hide();
      this.processing = false;
      console.error(err);
      const msg = err && err.error && err.error.message ? err.error.message : 'ประมวลผลไม่สำเร็จ';
      this.failDialog(typeof msg === 'string' ? msg : 'ประมวลผลไม่สำเร็จ');
    });
  }

  // ─── ดาวน์โหลดรูปผลลัพธ์ ─────────────────────────────────────────
  download() {
    if (!this.resultUrl) { return; }
    this.spinner.show();
    this.faceSwapService.getFileAsBlob(this.resultUrl).subscribe(blob => {
      this.spinner.hide();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `face-swap-${Date.now()}.png`;
      a.click();
      URL.revokeObjectURL(url);
    }, err => {
      this.spinner.hide();
      console.error(err);
      this.failDialog('ดาวน์โหลดไม่สำเร็จ');
    });
  }

  // ─── แชร์รูปผลลัพธ์ (Web Share API) ──────────────────────────────
  async share() {
    if (!this.resultUrl) { return; }
    const nav = navigator as any;
    if (!nav.share) {
      this.failDialog('Browser ของคุณไม่รองรับการแชร์ กรุณาใช้งานบนมือถือ');
      return;
    }
    try {
      this.spinner.show();
      const blob = await this.faceSwapService.getFileAsBlob(this.resultUrl).toPromise();
      const files: File[] = [new File([blob], 'face-swap.png', { type: 'image/png' })];
      this.spinner.hide();

      if (nav.canShare && nav.canShare({ files })) {
        await nav.share({ title: 'รูป Face Swap', text: 'แชร์รูป Face Swap', files });
      } else {
        await nav.share({ title: 'รูป Face Swap', text: this.resultUrl });
      }
    } catch (error) {
      this.spinner.hide();
      const err = error as any;
      if (err && err.name !== 'AbortError') {
        console.error(error);
        this.failDialog('เกิดข้อผิดพลาดในการแชร์');
      }
    }
  }

  // ─── เริ่มใหม่ ───────────────────────────────────────────────────
  reset() {
    this.faceFile = null;
    this.facePreview = null;
    this.targetFile = null;
    this.targetPreview = null;
    this.resultUrl = null;
  }

  // ─── Helpers ─────────────────────────────────────────────────────
  private isValidImage(file: File): boolean {
    const allowed = ['image/jpeg', 'image/jpg', 'image/png'];
    if (allowed.indexOf(file.type) === -1) {
      this.failDialog('รองรับเฉพาะไฟล์ JPG / PNG');
      return false;
    }
    const maxBytes = 10 * 1024 * 1024; // 10MB
    if (file.size > maxBytes) {
      this.failDialog('ขนาดไฟล์ต้องไม่เกิน 10MB');
      return false;
    }
    return true;
  }

  private readPreview(file: File, cb: (dataUrl: string) => void) {
    const reader = new FileReader();
    reader.onload = () => cb(reader.result as string);
    reader.readAsDataURL(file);
  }

  private successDialog() {
    Swal.fire('ทำรายการสำเร็จ!', '', 'success');
  }

  private failDialog(msg: string) {
    Swal.fire({ type: 'error', title: 'เกิดข้อผิดพลาด', text: msg });
  }
}
